import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  ArrowLeftRight,
  Award,
  Banknote,
  BarChart3,
  BookOpen,
  Building2,
  Calculator,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock,
  DollarSign,
  Filter,
  FileCheck,
  FileText,
  FlaskConical,
  GitBranch,
  GraduationCap,
  LayoutDashboard,
  LineChart,
  Network,
  Percent,
  PieChart,
  Send,
  Settings,
  Shield,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
  Layers,
  SlidersHorizontal,
  X,
} from "lucide-react";

const isBudgetOverviewPath = (pathname) => {
  if (pathname === "/budget") return true;
  if (!pathname.startsWith("/budget/")) return false;
  const excludedPrefixes = [
    "/budget/scenarios",
    "/budget/vs-actual",
    "/budget/margins",
    "/budget/revenue-recognition",
    "/budget/book",
  ];
  return !excludedPrefixes.some((prefix) => pathname.startsWith(prefix));
};

const MODULE_CONFIG = {
  tuition: {
    label: "Tuition",
    title: "Module",
    description: "Plan tuition, fees, and enrollment scenarios.",
    headerIcon: GraduationCap,
    path: "/fees/projection",
    storageKey: "tuitionSideNavCollapsed",
    theme: {
      border: "border-indigo-100/70 dark:border-slate-700/70",
      gradient:
        "bg-gradient-to-br from-indigo-50 via-blue-50 to-violet-100/70 dark:from-slate-900 dark:via-blue-950 dark:to-indigo-950/40",
      glowPrimary: "bg-indigo-200/40 dark:bg-indigo-500/10",
      glowSecondary: "bg-violet-200/30 dark:bg-violet-500/10",
      headerKicker: "text-indigo-700 dark:text-indigo-300",
      headerIconBg: "bg-indigo-600",
      toggleBorder: "border-indigo-200/70 dark:border-slate-700/70",
      toggleText: "text-indigo-700 dark:text-indigo-200",
      activeBorder: "border-indigo-200 dark:border-indigo-900/60",
      activeText: "text-indigo-700 dark:text-indigo-200",
      iconActiveBg: "bg-indigo-100 dark:bg-indigo-900/40",
      iconActiveText: "text-indigo-700 dark:text-indigo-200",
      accentBar: "bg-indigo-500",
    },
    items: [
      {
        label: "Fee Projection",
        path: "/fees/projection",
        icon: LineChart,
        isActive: (pathname) => pathname.startsWith("/fees/projection"),
      },
      {
        label: "Cost of Attendance",
        path: "/fees/cost-of-attendance",
        icon: Wallet,
        isActive: (pathname) => pathname.startsWith("/fees/cost-of-attendance"),
      },
      {
        label: "Fee Components",
        path: "/fees/components",
        icon: Layers,
        isActive: (pathname) => pathname.startsWith("/fees/components"),
      },
      {
        label: "Discount Policies",
        path: "/fees/discounts",
        icon: DollarSign,
        isActive: (pathname) => pathname.startsWith("/fees/discounts"),
      },
      {
        label: "Enrollment Funnel",
        path: "/enrollment/funnel",
        icon: Filter,
        isActive: (pathname) => pathname.startsWith("/enrollment/funnel"),
      },
      {
        label: "Yield Analysis",
        path: "/enrollment/yield",
        icon: TrendingUp,
        isActive: (pathname) => pathname.startsWith("/enrollment/yield"),
      },
    ],
  },
  research: {
    label: "Research",
    title: "Module",
    description: "Track commitments, settings, and portfolio view.",
    headerIcon: FlaskConical,
    path: "/research",
    storageKey: "researchSideNavCollapsed",
    theme: {
      border: "border-blue-100/70 dark:border-slate-700/70",
      gradient:
        "bg-gradient-to-br from-blue-50 via-indigo-50 to-indigo-100/70 dark:from-slate-900 dark:via-blue-950 dark:to-indigo-950/40",
      glowPrimary: "bg-blue-200/40 dark:bg-blue-500/10",
      glowSecondary: "bg-indigo-200/30 dark:bg-indigo-500/10",
      headerKicker: "text-blue-700 dark:text-blue-300",
      headerIconBg: "bg-blue-600",
      toggleBorder: "border-blue-200/70 dark:border-slate-700/70",
      toggleText: "text-blue-700 dark:text-blue-200",
      activeBorder: "border-blue-200 dark:border-blue-900/60",
      activeText: "text-blue-700 dark:text-blue-200",
      iconActiveBg: "bg-blue-100 dark:bg-blue-900/40",
      iconActiveText: "text-blue-700 dark:text-blue-200",
      accentBar: "bg-indigo-500",
    },
    sections: [
      {
        key: "overview",
        items: [
          {
            label: "Awards",
            path: "/research",
            icon: FlaskConical,
            isActive: (pathname) =>
              pathname.startsWith("/research") &&
              !pathname.startsWith("/research/portfolio") &&
              !pathname.startsWith("/research/subawards") &&
              !pathname.startsWith("/research/proposals") &&
              !pathname.startsWith("/research/explorer") &&
              !pathname.startsWith("/research/analytics") &&
              !pathname.startsWith("/research/financial-health") &&
              !pathname.startsWith("/research/portfolio-performance") &&
              !pathname.startsWith("/research/compliance-risk") &&
              !pathname.startsWith("/research/personnel-effort") &&
              !pathname.startsWith("/research/closeout-readiness") &&
              !pathname.startsWith("/research/workload") &&
              !pathname.startsWith("/research/limited-submissions") &&
              !pathname.startsWith("/research/jit-requests") &&
              !pathname.startsWith("/research/pas-requests") &&
              !pathname.startsWith("/research/document-rules") &&
              !pathname.startsWith("/research/approval-workflows") &&
              !pathname.startsWith("/research/training-compliance") &&
              !pathname.startsWith("/research/fa-waiver-requests") &&
              !pathname.startsWith("/research/submission-settings") &&
              !pathname.startsWith("/research/settings") &&
              !pathname.endsWith("/settings"),
          },
          {
            label: "Proposals",
            path: "/research/proposals",
            icon: Send,
            isActive: (pathname) => pathname.startsWith("/research/proposals"),
          },
          {
            label: "Subawards",
            path: "/research/subawards",
            icon: Users,
            isActive: (pathname) => pathname.startsWith("/research/subawards"),
          },
          {
            label: "Explorer",
            path: "/research/explorer",
            icon: Network,
            isActive: (pathname) => pathname.startsWith("/research/explorer"),
          },
          {
            label: "Portfolio",
            path: "/research/portfolio",
            icon: BarChart3,
            isActive: (pathname) => pathname.startsWith("/research/portfolio"),
          },
          {
            label: "Workload",
            path: "/research/workload",
            icon: Users,
            isActive: (pathname) => pathname.startsWith("/research/workload"),
          },
        ],
      },
      {
        key: "pre-award",
        label: "Pre-Award",
        collapsible: true,
        storageKey: "researchPreAwardSectionCollapsed",
        items: [
          {
            label: "Limited Submissions",
            path: "/research/limited-submissions",
            icon: Trophy,
            isActive: (pathname) => pathname.startsWith("/research/limited-submissions"),
          },
          {
            label: "JIT Requests",
            path: "/research/jit-requests",
            icon: Clock,
            isActive: (pathname) => pathname.startsWith("/research/jit-requests"),
          },
          {
            label: "Pre-Award Spending",
            path: "/research/pas-requests",
            icon: Banknote,
            isActive: (pathname) => pathname.startsWith("/research/pas-requests"),
          },
          {
            label: "Document Rules",
            path: "/research/document-rules",
            icon: FileCheck,
            isActive: (pathname) => pathname.startsWith("/research/document-rules"),
          },
        ],
      },
      {
        key: "analytics",
        label: "Analytics",
        collapsible: true,
        storageKey: "researchAnalyticsSectionCollapsed",
        items: [
          {
            label: "Proposal Analytics",
            path: "/research/analytics",
            icon: TrendingUp,
            isActive: (pathname) => pathname.startsWith("/research/analytics"),
          },
          { type: "divider", label: "Post-Award Analytics" },
          {
            label: "Financial Health",
            path: "/research/financial-health",
            icon: DollarSign,
            isActive: (pathname) =>
              pathname.startsWith("/research/financial-health"),
          },
          {
            label: "Portfolio Performance",
            path: "/research/portfolio-performance",
            icon: PieChart,
            isActive: (pathname) =>
              pathname.startsWith("/research/portfolio-performance"),
          },
          {
            label: "Compliance & Risk",
            path: "/research/compliance-risk",
            icon: FileText,
            isActive: (pathname) =>
              pathname.startsWith("/research/compliance-risk"),
          },
          {
            label: "Personnel & Effort",
            path: "/research/personnel-effort",
            icon: Users,
            isActive: (pathname) =>
              pathname.startsWith("/research/personnel-effort"),
          },
          {
            label: "Closeout Readiness",
            path: "/research/closeout-readiness",
            icon: Calculator,
            isActive: (pathname) =>
              pathname.startsWith("/research/closeout-readiness"),
          },
        ],
      },
      {
        key: "compliance",
        label: "Compliance & Admin",
        collapsible: true,
        storageKey: "researchComplianceSectionCollapsed",
        items: [
          {
            label: "Training Compliance",
            path: "/research/training-compliance",
            icon: Award,
            isActive: (pathname) =>
              pathname.startsWith("/research/training-compliance"),
          },
          {
            label: "F&A Waiver Requests",
            path: "/research/fa-waiver-requests",
            icon: Percent,
            isActive: (pathname) =>
              pathname.startsWith("/research/fa-waiver-requests"),
          },
          {
            label: "Approval Workflows",
            path: "/research/approval-workflows",
            icon: GitBranch,
            isActive: (pathname) =>
              pathname.startsWith("/research/approval-workflows"),
          },
          {
            label: "Submission Settings",
            path: "/research/submission-settings",
            icon: SlidersHorizontal,
            isActive: (pathname) =>
              pathname.startsWith("/research/submission-settings"),
          },
        ],
      },
      {
        key: "settings",
        items: [
          {
            label: "Settings",
            path: "/research/settings",
            icon: Settings,
            isActive: (pathname) =>
              pathname.startsWith("/research/settings") ||
              pathname.endsWith("/settings"),
          },
        ],
      },
    ],
  },
  reporting: {
    label: "Analytics BI",
    title: "Module",
    description: "BI dashboards and reporting tools.",
    headerIcon: BarChart3,
    path: "/superset",
    storageKey: "reportingSideNavCollapsed",
    theme: {
      border: "border-sky-100/70 dark:border-slate-700/70",
      gradient:
        "bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-100/70 dark:from-slate-900 dark:via-sky-950 dark:to-cyan-950/40",
      glowPrimary: "bg-sky-200/35 dark:bg-sky-500/10",
      glowSecondary: "bg-cyan-200/30 dark:bg-cyan-500/10",
      headerKicker: "text-sky-700 dark:text-sky-300",
      headerIconBg: "bg-sky-600",
      toggleBorder: "border-sky-200/70 dark:border-slate-700/70",
      toggleText: "text-sky-700 dark:text-sky-200",
      activeBorder: "border-sky-200 dark:border-sky-900/60",
      activeText: "text-sky-700 dark:text-sky-200",
      iconActiveBg: "bg-sky-100 dark:bg-sky-900/40",
      iconActiveText: "text-sky-700 dark:text-sky-200",
      accentBar: "bg-sky-500",
    },
    sections: [
      {
        label: "Analytics BI",
        items: [
          {
            label: "Analytics BI",
            path: "/superset",
            icon: BarChart3,
            isActive: (pathname) => pathname.startsWith("/superset"),
            variant: "featured",
            featured: {
              base: "border-indigo-200/70 bg-gradient-to-r from-white via-indigo-50 to-blue-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100/80 hover:shadow-md dark:border-indigo-900/60 dark:bg-slate-900/70 dark:from-slate-950 dark:via-indigo-950 dark:to-slate-900 dark:text-indigo-100 dark:ring-indigo-900/40",
              active:
                "border-indigo-300 bg-white text-indigo-800 shadow-md ring-1 ring-indigo-200/80 dark:border-indigo-900/70 dark:bg-slate-900/80 dark:text-indigo-100 dark:ring-indigo-900/50",
            },
          },
        ],
      },
      {
        label: "Analytics & Reporting",
        items: [
          {
            label: "Budget vs Actual",
            path: "/budget/vs-actual",
            icon: BarChart3,
            isActive: (pathname) => pathname.startsWith("/budget/vs-actual"),
          },
          {
            label: "Program Margins",
            path: "/budget/margins",
            icon: PieChart,
            isActive: (pathname) => pathname.startsWith("/budget/margins"),
          },
          {
            label: "Revenue Recognition",
            path: "/budget/revenue-recognition",
            icon: DollarSign,
            isActive: (pathname) =>
              pathname.startsWith("/budget/revenue-recognition"),
          },
          {
            label: "Budget Book",
            path: "/budget/book",
            icon: FileText,
            isActive: (pathname) => pathname.startsWith("/budget/book"),
          },
        ],
      },
    ],
    relatedLinks: [
      {
        label: "Back to Budget Planning",
        path: "/planning",
        icon: LineChart,
      },
    ],
  },
  courses: {
    label: "Courses",
    title: "Module",
    description: "Schedule courses and manage capacity.",
    headerIcon: BookOpen,
    path: "/courses",
    storageKey: "coursesSideNavCollapsed",
    theme: {
      border: "border-amber-100/70 dark:border-slate-700/70",
      gradient:
        "bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100/70 dark:from-slate-900 dark:via-amber-950 dark:to-orange-950/40",
      glowPrimary: "bg-amber-200/40 dark:bg-amber-500/10",
      glowSecondary: "bg-orange-200/30 dark:bg-orange-500/10",
      headerKicker: "text-amber-700 dark:text-amber-300",
      headerIconBg: "bg-amber-600",
      toggleBorder: "border-amber-200/70 dark:border-slate-700/70",
      toggleText: "text-amber-700 dark:text-amber-200",
      activeBorder: "border-amber-200 dark:border-amber-900/60",
      activeText: "text-amber-700 dark:text-amber-200",
      iconActiveBg: "bg-amber-100 dark:bg-amber-900/40",
      iconActiveText: "text-amber-700 dark:text-amber-200",
      accentBar: "bg-amber-500",
    },
    items: [
      {
        label: "Course Planning",
        path: "/courses",
        icon: BookOpen,
        isActive: (pathname) => pathname.startsWith("/courses"),
      },
    ],
  },
  positions: {
    label: "Positions",
    title: "Module",
    description: "Plan positions, budgets, and staffing.",
    headerIcon: Users,
    path: "/positions/budgeting",
    storageKey: "positionsSideNavCollapsed",
    theme: {
      border: "border-purple-100/70 dark:border-slate-700/70",
      gradient:
        "bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-100/70 dark:from-slate-900 dark:via-indigo-950 dark:to-blue-950/40",
      glowPrimary: "bg-purple-200/40 dark:bg-purple-500/10",
      glowSecondary: "bg-blue-200/30 dark:bg-blue-500/10",
      headerKicker: "text-purple-700 dark:text-purple-300",
      headerIconBg: "bg-purple-600",
      toggleBorder: "border-purple-200/70 dark:border-slate-700/70",
      toggleText: "text-purple-700 dark:text-purple-200",
      activeBorder: "border-purple-200 dark:border-purple-900/60",
      activeText: "text-purple-700 dark:text-purple-200",
      iconActiveBg: "bg-purple-100 dark:bg-purple-900/40",
      iconActiveText: "text-purple-700 dark:text-purple-200",
      accentBar: "bg-purple-500",
    },
    items: [
      {
        label: "Position Budgeting",
        path: "/positions/budgeting",
        icon: Users,
        isActive: (pathname) => pathname.startsWith("/positions/budgeting"),
      },
      {
        label: "Transfers",
        path: "/transfers",
        icon: ArrowLeftRight,
        isActive: (pathname) => pathname.startsWith("/transfers"),
      },
      {
        label: "Allocations",
        path: "/allocations",
        icon: SlidersHorizontal,
        isActive: (pathname) => pathname.startsWith("/allocations"),
      },
      {
        label: "Approvals",
        path: "/approvals",
        icon: ClipboardCheck,
        isActive: (pathname) => pathname.startsWith("/approvals"),
      },
      {
        label: "My Submissions",
        path: "/my-submissions",
        icon: Send,
        isActive: (pathname) => pathname.startsWith("/my-submissions"),
      },
    ],
  },
  budget: {
    label: "Budget",
    title: "Module",
    description: "Build plans and review budget performance.",
    headerIcon: Wallet,
    path: "/budget",
    storageKey: "budgetSideNavCollapsed",
    theme: {
      border: "border-blue-100/70 dark:border-slate-700/70",
      gradient:
        "bg-gradient-to-br from-blue-50 via-indigo-50 to-emerald-100/70 dark:from-slate-900 dark:via-indigo-950 dark:to-emerald-950/40",
      glowPrimary: "bg-blue-200/35 dark:bg-blue-500/10",
      glowSecondary: "bg-emerald-200/35 dark:bg-emerald-500/10",
      headerKicker: "text-blue-700 dark:text-blue-300",
      headerIconBg: "bg-indigo-600",
      toggleBorder: "border-blue-200/70 dark:border-slate-700/70",
      toggleText: "text-blue-700 dark:text-blue-200",
      activeBorder: "border-blue-200 dark:border-blue-900/60",
      activeText: "text-blue-700 dark:text-blue-200",
      iconActiveBg: "bg-blue-100 dark:bg-blue-900/40",
      iconActiveText: "text-blue-700 dark:text-blue-200",
      accentBar: "bg-indigo-500",
    },
    sections: [
      {
        label: "Planning Core",
        items: [
          {
            label: "Budget Overview",
            path: "/budget",
            icon: LayoutDashboard,
            isActive: isBudgetOverviewPath,
          },
          {
            label: "Planning",
            path: "/planning",
            icon: LineChart,
            isActive: (pathname) => pathname.startsWith("/planning"),
          },
          {
            label: "Budget Model",
            path: "/budget-model",
            icon: Calculator,
            isActive: (pathname) => pathname.startsWith("/budget-model"),
          },
          {
            label: "Budget Scenarios",
            path: "/budget/scenarios",
            icon: GitBranch,
            isActive: (pathname) => pathname.startsWith("/budget/scenarios"),
          },
        ],
      },
    ],
  },
};

const MODULE_SWITCHER_ORDER = [
  "budget",
  "positions",
  "research",
  "tuition",
  "reporting",
  "courses",
  "capital",
];

const MODULE_SWITCHER_ITEMS = MODULE_SWITCHER_ORDER.map((key) => {
  if (key === "capital") {
    return {
      key,
      label: "Capital",
      path: "/capital/planning",
      icon: Building2,
    };
  }
  const config = MODULE_CONFIG[key];
  if (!config?.path) return null;
  return {
    key,
    label: config.label,
    path: config.path,
    icon: config.headerIcon,
  };
}).filter(Boolean);

const ModuleSideNav = ({ moduleKey, isMobileOpen, onMobileClose }) => {
  const location = useLocation();
  const config = MODULE_CONFIG[moduleKey];
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const switcherRef = useRef(null);
  const activeSwitcherItemRef = useRef(null);
  const autoScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!config) return;
    try {
      const saved = window.localStorage.getItem(config.storageKey);
      if (saved === "true") {
        setCollapsed(true);
      }
    } catch (_) {
      // ignore localStorage errors
    }
  }, [config]);

  useEffect(() => {
    if (!config?.sections) {
      setCollapsedSections({});
      return;
    }
    const next = {};
    config.sections.forEach((section, index) => {
      if (!section.collapsible) return;
      const sectionKey = section.key ?? section.label ?? `section-${index}`;
      let isCollapsed = Boolean(section.defaultCollapsed);
      const storageKey =
        section.storageKey ?? `${config.storageKey}:${sectionKey}`;
      try {
        const saved = window.localStorage.getItem(storageKey);
        if (saved === "true") {
          isCollapsed = true;
        } else if (saved === "false") {
          isCollapsed = false;
        }
      } catch (_) {
        // ignore localStorage errors
      }
      next[sectionKey] = isCollapsed;
    });
    setCollapsedSections(next);
  }, [config]);

  // Close mobile sidebar on route change
  useEffect(() => {
    if (isMobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        if (config) {
          window.localStorage.setItem(config.storageKey, String(next));
        }
      } catch (_) {
        // ignore localStorage errors
      }
      return next;
    });
  };

  const toggleSection = (sectionKey, storageKey, defaultCollapsed = false) => {
    setCollapsedSections((prev) => {
      const current = prev[sectionKey] ?? defaultCollapsed;
      const next = !current;
      try {
        if (storageKey) {
          window.localStorage.setItem(storageKey, String(next));
        }
      } catch (_) {
        // ignore localStorage errors
      }
      return { ...prev, [sectionKey]: next };
    });
  };

  const updateSwitcherButtons = useCallback(() => {
    const container = switcherRef.current;
    if (!container) return;
    // Skip if container hasn't been laid out yet
    if (container.clientWidth === 0) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      maxScrollLeft > 0 && container.scrollLeft < maxScrollLeft - 1,
    );
  }, []);

  useEffect(() => {
    if (collapsed) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return undefined;
    }
    updateSwitcherButtons();
    const container = switcherRef.current;
    if (!container) return undefined;
    const handleScroll = () => updateSwitcherButtons();
    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [collapsed, updateSwitcherButtons]);

  useLayoutEffect(() => {
    if (collapsed) return;
    updateSwitcherButtons();
  }, [collapsed, moduleKey, updateSwitcherButtons]);

  useEffect(() => {
    if (collapsed) return;
    // Use multiple frames to ensure layout is complete
    let frame1, frame2, timer;
    frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        updateSwitcherButtons();
      });
    });
    timer = window.setTimeout(() => updateSwitcherButtons(), 300);
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
      window.clearTimeout(timer);
    };
  }, [collapsed, moduleKey, updateSwitcherButtons]);

  useEffect(() => {
    if (collapsed) return;
    const container = switcherRef.current;
    if (!container || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => updateSwitcherButtons());
    observer.observe(container);
    return () => observer.disconnect();
  }, [collapsed, updateSwitcherButtons]);

  useEffect(() => {
    if (collapsed || !document?.fonts?.ready) return undefined;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) updateSwitcherButtons();
    });
    return () => {
      cancelled = true;
    };
  }, [collapsed, updateSwitcherButtons]);

  const scrollSwitcher = (direction) => {
    const container = switcherRef.current;
    if (!container) return;
    const delta = Math.max(100, Math.floor(container.clientWidth * 0.5));
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const newScrollLeft = Math.min(
      Math.max(container.scrollLeft + direction * delta, 0),
      maxScrollLeft,
    );
    container.scrollTo({ left: newScrollLeft, behavior: "smooth" });
    // Update buttons after scroll completes
    setTimeout(updateSwitcherButtons, 150);
  };

  const centerActiveModule = useCallback((behavior = "auto") => {
    const container = switcherRef.current;
    const activeItem = activeSwitcherItemRef.current;
    if (!container || !activeItem) return;
    const containerRect = container.getBoundingClientRect();
    const activeRect = activeItem.getBoundingClientRect();
    if (containerRect.width === 0 || activeRect.width === 0) return;
    const activeCenter = activeRect.left + activeRect.width / 2;
    const containerCenter = containerRect.left + containerRect.width / 2;
    const offset = activeCenter - containerCenter;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const target = Math.min(
      Math.max(container.scrollLeft + offset, 0),
      maxScrollLeft,
    );
    if (Math.abs(container.scrollLeft - target) < 1) return;
    container.scrollTo({ left: target, behavior });
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (!autoScrollRef.current) return;
    window.clearInterval(autoScrollRef.current);
    autoScrollRef.current = null;
  }, []);

  const startAutoScroll = useCallback(
    (direction) => {
      if (autoScrollRef.current) return;
      const container = switcherRef.current;
      if (!container) return;
      autoScrollRef.current = window.setInterval(() => {
        const activeContainer = switcherRef.current;
        if (!activeContainer) return;
        const maxScrollLeft =
          activeContainer.scrollWidth - activeContainer.clientWidth;
        const next = Math.min(
          Math.max(activeContainer.scrollLeft + direction * 3, 0),
          maxScrollLeft,
        );
        if (next === activeContainer.scrollLeft) {
          stopAutoScroll();
          return;
        }
        activeContainer.scrollTo({ left: next, behavior: "auto" });
        updateSwitcherButtons();
      }, 16);
    },
    [stopAutoScroll, updateSwitcherButtons],
  );

  useEffect(() => {
    return () => stopAutoScroll();
  }, [stopAutoScroll]);

  useLayoutEffect(() => {
    if (collapsed) return;
    const frame = requestAnimationFrame(() => centerActiveModule("auto"));
    return () => cancelAnimationFrame(frame);
  }, [centerActiveModule, collapsed, moduleKey]);

  if (!config) return null;

  const Icon = config.headerIcon;
  const theme = config.theme;

  const renderItems = (items, { id } = {}) => (
    <div
      id={id}
      className={`flex flex-col gap-2 ${collapsed ? "md:items-center" : ""}`}
    >
      {items.map((item, idx) => {
        // Handle divider type
        if (item.type === "divider") {
          if (collapsed) return null;
          return (
            <div key={`divider-${idx}`} className="pt-2 pb-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 px-3">
                {item.label}
              </div>
            </div>
          );
        }

        const active = item.isActive
          ? item.isActive(location.pathname)
          : location.pathname.startsWith(item.path);
        const ItemIcon = item.icon;
        const isFeatured = item.variant === "featured";
        const featuredBase =
          item.featured?.base ??
          `${theme.activeBorder} bg-white/80 ${theme.activeText} shadow-sm`;
        const featuredActive =
          item.featured?.active ??
          `${theme.activeBorder} bg-white/90 ${theme.activeText} shadow-md`;
        const baseClasses = isFeatured
          ? featuredBase
          : "border-transparent text-slate-600 hover:bg-white/70 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/60 dark:hover:text-slate-200";
        const activeClasses = isFeatured
          ? featuredActive
          : `${theme.activeBorder} bg-white/80 ${theme.activeText} shadow-sm dark:bg-slate-900/70`;
        return (
          <NavLink
            key={item.path}
            to={item.path}
            title={collapsed ? item.label : undefined}
            aria-label={collapsed ? item.label : undefined}
            className={`group relative flex rounded-xl border px-3 py-3 text-sm font-semibold transition touch-target ${
              collapsed
                ? "md:min-w-[56px] justify-center"
                : "items-center gap-3"
            } ${active ? activeClasses : baseClasses}`}
          >
            {active && (
              <span
                className={`absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-full ${theme.accentBar}`}
              />
            )}
            <span
              className={`rounded-lg p-1.5 ${
                active || isFeatured
                  ? `${theme.iconActiveBg} ${theme.iconActiveText}`
                  : "bg-white/80 text-slate-500 group-hover:bg-white dark:bg-slate-800 dark:text-slate-300 dark:group-hover:bg-slate-700/80 dark:group-hover:text-slate-200"
              }`}
            >
              <ItemIcon className="h-5 w-5" />
            </span>
            {!collapsed && (
              <span className="whitespace-nowrap">{item.label}</span>
            )}
          </NavLink>
        );
      })}
    </div>
  );

  // Create a function to render content so refs work properly
  const renderSidebarContent = (useRef = false) => (
    <div
      className={`group/sidebar relative overflow-hidden rounded-2xl border ${theme.border} ${theme.gradient} p-4 shadow-xl md:sticky md:top-24 md:flex md:max-h-[calc(100vh-6rem)] md:flex-col ${
        collapsed ? "md:overflow-visible" : ""
      }`}
    >
      <div
        className={`absolute -right-6 -top-6 h-20 w-20 rounded-full ${theme.glowPrimary} blur-2xl`}
      />
      <div
        className={`absolute -left-6 top-14 h-16 w-16 rounded-full ${theme.glowSecondary} blur-2xl`}
      />
      <div className="relative md:flex md:flex-1 md:min-h-0 md:flex-col">
        <div
          className={`flex items-start justify-between gap-2 ${
            collapsed
              ? "md:flex-col md:items-center md:justify-start md:gap-3"
              : ""
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`rounded-xl p-2 text-white shadow-lg shadow-black/20 ${theme.headerIconBg}`}
            >
              <Icon className="h-5 w-5" />
            </span>
            {!collapsed && (
              <div>
                <div
                  className={`text-[10px] font-semibold uppercase tracking-[0.3em] ${theme.headerKicker}`}
                >
                  {config.label}
                </div>
                <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {config.title}
                </div>
              </div>
            )}
          </div>
          {/* Close button for mobile */}
          {isMobileOpen && (
            <button
              type="button"
              className={`md:hidden rounded-lg border ${theme.toggleBorder} bg-white/80 p-2 ${theme.toggleText} shadow-sm transition hover:bg-white dark:bg-slate-900/70 touch-target`}
              onClick={onMobileClose}
              aria-label="Close navigation"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {/* Collapse button for desktop */}
          <div
            className={`relative hidden md:block ${
              collapsed
                ? "md:absolute md:-right-7 md:top-3 md:z-20 md:translate-y-1 md:opacity-0 md:pointer-events-none md:group-hover/sidebar:translate-y-0 md:group-hover/sidebar:opacity-100 md:group-hover/sidebar:pointer-events-auto md:focus-within:translate-y-0 md:focus-within:opacity-100 md:focus-within:pointer-events-auto"
                : ""
            }`}
          >
            <button
              type="button"
              className={`peer rounded-lg border ${theme.toggleBorder} bg-white/80 p-2 ${theme.toggleText} shadow-sm transition hover:bg-white dark:bg-slate-900/70`}
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
            {collapsed && (
              <span className="pointer-events-none absolute left-1/2 top-full mt-2 z-20 hidden -translate-x-1/2 translate-y-1 whitespace-nowrap rounded-full bg-slate-900/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white shadow-lg opacity-0 transition md:block peer-hover:translate-y-0 peer-hover:opacity-100 peer-focus-visible:translate-y-0 peer-focus-visible:opacity-100 dark:bg-slate-100 dark:text-slate-900">
                Expand navigation
              </span>
            )}
          </div>
        </div>
        {!collapsed && (
          <>
            <nav className="mt-3" aria-label="Switch modules">
              <div className="module-switcher relative flex items-center">
                <button
                  type="button"
                  className={`module-switcher-chevron shrink-0 ${
                    canScrollLeft ? "" : "opacity-30"
                  }`}
                  onClick={() => scrollSwitcher(-1)}
                  onMouseEnter={() => startAutoScroll(-1)}
                  onMouseLeave={stopAutoScroll}
                  aria-label="Scroll modules left"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div
                  ref={useRef ? switcherRef : null}
                  className="flex-1 min-w-0 overflow-x-auto mx-1 py-1 scrollbar-hide rounded-full border border-slate-200/70 bg-slate-50/80 dark:border-slate-700/60 dark:bg-slate-900/50"
                  style={{ maxWidth: "calc(100% - 70px)" }}
                >
                  <div className="flex w-max items-center gap-2 px-1">
                    {MODULE_SWITCHER_ITEMS.map((module) => {
                      const active = module.key === moduleKey;
                      const ModuleIcon = module.icon;
                      return (
                        <Link
                          key={module.key}
                          to={module.path}
                          ref={active ? activeSwitcherItemRef : null}
                          aria-current={active ? "page" : undefined}
                          className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                            active
                              ? `${theme.activeBorder} ${theme.activeText} bg-white/90 shadow-sm dark:bg-slate-900/80`
                              : "border-transparent text-slate-600 hover:bg-white/70 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/60 dark:hover:text-slate-200"
                          }`}
                        >
                          <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full ${
                              active
                                ? `${theme.iconActiveBg} ${theme.iconActiveText}`
                                : "bg-white/80 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
                            }`}
                          >
                            <ModuleIcon className="h-3.5 w-3.5" />
                          </span>
                          <span className="whitespace-nowrap">
                            {module.label}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  className={`module-switcher-chevron shrink-0 ${
                    canScrollRight ? "" : "opacity-30"
                  }`}
                  onClick={() => scrollSwitcher(1)}
                  onMouseEnter={() => startAutoScroll(1)}
                  onMouseLeave={stopAutoScroll}
                  aria-label="Scroll modules right"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </nav>
            <div className="mt-2 text-xs text-slate-600 dark:text-slate-300">
              {config.description}
            </div>
          </>
        )}
        <div className="mt-4 md:flex-1 md:min-h-0 md:overflow-y-auto md:pr-1">
          <nav>
            {config.sections && config.sections.length > 0 ? (
              <div className="space-y-3">
                {config.sections.map((section, index) => {
                  const sectionKey =
                    section.key ?? section.label ?? `section-${index}`;
                  const sectionId = `${moduleKey}-section-${String(sectionKey)
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9-_]+/g, "-")}`;
                  const storageKey = section.storageKey
                    ? section.storageKey
                    : config.storageKey
                      ? `${config.storageKey}:${sectionKey}`
                      : null;
                  const isCollapsed =
                    collapsedSections[sectionKey] ??
                    Boolean(section.defaultCollapsed);
                  const hasLabel = Boolean(section.label);
                  const showToggle =
                    hasLabel && section.collapsible && !collapsed;
                  return (
                    <div
                      key={sectionKey}
                      className={collapsed || !section.label ? "" : "space-y-2"}
                    >
                      {!collapsed && hasLabel && !section.collapsible && (
                        <div className="px-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">
                          {section.label}
                        </div>
                      )}
                      {showToggle && (
                        <button
                          type="button"
                          className="flex w-full items-center justify-between px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 transition-colors hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                          onClick={() =>
                            toggleSection(
                              sectionKey,
                              storageKey,
                              section.defaultCollapsed,
                            )
                          }
                          aria-expanded={!isCollapsed}
                          aria-controls={sectionId}
                        >
                          <span>{section.label}</span>
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${
                              isCollapsed ? "-rotate-90" : ""
                            }`}
                          />
                        </button>
                      )}
                      {!isCollapsed &&
                        renderItems(section.items, { id: sectionId })}
                    </div>
                  );
                })}
              </div>
            ) : (
              renderItems(config.items || [])
            )}
          </nav>
          {config.relatedLinks && config.relatedLinks.length > 0 && (
            <div className="mt-4">
              {!collapsed && (
                <div className="px-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Related
                </div>
              )}
              <div
                className={`mt-2 flex ${
                  collapsed ? "justify-center" : "flex-col gap-2"
                }`}
              >
                {config.relatedLinks.map((link) => {
                  const LinkIcon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      title={collapsed ? link.label : undefined}
                      aria-label={collapsed ? link.label : undefined}
                      className={`group flex items-center gap-3 rounded-xl border border-dashed px-3 py-3 text-sm font-semibold transition touch-target ${
                        collapsed ? "justify-center" : ""
                      } border-slate-200/80 bg-white/70 ${
                        theme.activeText
                      } hover:bg-white hover:text-slate-900 dark:border-slate-700/70 dark:bg-slate-900/60 dark:text-slate-200 dark:hover:bg-slate-900`}
                    >
                      <span
                        className={`rounded-lg p-1.5 ${theme.iconActiveBg} ${theme.iconActiveText}`}
                      >
                        <LinkIcon className="h-5 w-5" />
                      </span>
                      {!collapsed && (
                        <span className="whitespace-nowrap">
                          {link.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:block ${collapsed ? "md:w-20" : "md:w-64"} shrink-0`}
      >
        {renderSidebarContent(true)}
      </aside>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="mobile-sidebar-overlay md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] overflow-y-auto bg-white dark:bg-slate-900 p-4 shadow-2xl md:hidden safe-area-bottom ${
          isMobileOpen ? "sidebar-slide-in" : "hidden"
        }`}
      >
        {renderSidebarContent(false)}
      </aside>
    </>
  );
};

export default ModuleSideNav;
